import { css } from '@emotion/react'
import { areas, usePrefectureLinks } from './dependencies'
import { FC } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { router } from '@lifedot/router'
import { StandardLonghandProperties } from 'csstype'
import { mq } from '@lifedot/styles/mediaQuery'
import { palette } from '@lifedot/styles/palette'
import { typography } from '@lifedot/styles/typography'

type PositionAndSize = Pick<
  StandardLonghandProperties,
  'top' | 'left' | 'right' | 'bottom' | 'width' | 'height'
>

const styles = {
  map: css({
    position: 'relative',
    width: 560,
    height: 446,
    p: {
      margin: 0
    },
    [mq('sp')]: {
      display: 'none'
    }
  }),
  title: css({
    position: 'absolute',
    left: 200,
    top: 188
  }),
  image: css({
    position: 'absolute',
    width: 240,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  }),
  regionName: css({
    textAlign: 'center',
    fontWeight: 'bold'
  }),
  regionBox: (arg: PositionAndSize) =>
    css({
      background: palette.gray1,
      position: 'absolute',
      padding: '8px 12px 4px',
      textAlign: 'left',
      borderRadius: 4,
      ...arg
    }),
  itemWrap: css({
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 4,
    li: {
      backgroundColor: palette.white,
      padding: 4,
      borderRadius: 4,
      margin: '0 2px 4px',
      width: 56,
      textAlign: 'center'
    }
  }),
  disabled: css({
    color: palette.gray5
  })
}

interface InnerMapProps {
  choiceCode?: string
}

export const InnerMap: FC<InnerMapProps> = ({ choiceCode }) => {
  const [disables, choice] = usePrefectureLinks(choiceCode)
  return (
    <div css={styles.map}>
      <div css={styles.image}>
        <Image
          src="https://s3.lifedot.jp/uploads/ending/path/67806/japan.png"
          width={504}
          height={423}
          alt="日本地図"
        />
      </div>
      <p css={[typography.textXL, styles.title]}>
        {choice?.name ? (
          <>
            {choice.name}
            <br />
            <small>を探す</small>
          </>
        ) : (
          <>
            エリア
            <br />
            <small>から探す</small>
          </>
        )}
      </p>
      {areas.map(({ label, prefectures }) => (
        <div key={label} css={styles.regionBox(positions[label] ?? {})}>
          <p css={[typography.textS, styles.regionName]}>{label}</p>
          <ul css={styles.itemWrap}>
            {prefectures.map(({ name, roma }) => (
              <li key={roma}>
                {!disables?.[roma] ? (
                  <Link
                    href={router.list({
                      prefectureRoma: roma,
                      condition: choice
                    })}
                    passHref
                  >
                    <a css={typography.textS}>{name}</a>
                  </Link>
                ) : (
                  <span css={[typography.textS, styles.disabled]}>{name}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

const positions: Record<string, PositionAndSize> = {
  '北海道・東北': { width: '144px', top: 0, right: 0 },
  関東: { width: '144px', top: '185px', right: 0 },
  東海: { width: '144px', bottom: 0, left: '268px' },
  '北陸・甲信越': {
    width: '204px',
    top: 0,
    left: '208px'
  },
  関西: {
    width: '204px',
    top: 0,
    left: 0
  },
  '中国・四国': {
    width: '144px',
    top: '114px',
    left: 0
  },
  '九州・沖縄': {
    width: '264px',
    bottom: 0,
    left: 0
  }
}
